// ResearchDevelopment.js - Adam International Academy Research Center
import React, { useState, useEffect, useRef } from 'react';
import './ResearchDevelopment.css';
import { Link } from 'react-router-dom';

// Görselleri import et
import img1 from '../../assets/Research & Development/IMG (1).jpg';
import img2 from '../../assets/Research & Development/IMG (2).jpg';
import img3 from '../../assets/Research & Development/IMG (3).jpg';
import img4 from '../../assets/Research & Development/IMG (4).jpg';
import img5 from '../../assets/Research & Development/IMG (5).jpg';
import img6 from '../../assets/Research & Development/IMG (6).jpg'; 
import img7 from '../../assets/Research & Development/IMG (7).jpg';
import img8 from '../../assets/Research & Development/IMG (8).jpg';
import img9 from '../../assets/Research & Development/IMG (9).jpg';
import img10 from '../../assets/Research & Development/IMG (10).jpg';
import collabImage from '../../assets/Research & Development/collaboration.jpg';
import FacilitiesImage1 from '../../assets/Research & Development/facilities1.jpg';
import FacilitiesImage2 from '../../assets/Research & Development/facilities2.jpg';
import FacilitiesImage3 from '../../assets/Research & Development/facilities3.jpg';
import heroImage from '../../assets/Research & Development/hero-image.jpg';

const ResearchDevelopment = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [selectedProject, setSelectedProject] = useState(null);
  const [visibleSections, setVisibleSections] = useState({});
  const sectionRefs = useRef({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.dataset.section]: true
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      Object.values(sectionRefs.current).forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Academic Research Areas from Adam Center documents
  const researchAreas = [
    {
      id: 1,
      title: 'Sustainable Development Research',
      description: 'Academic research on SDG implementation, sustainability metrics, and development policy analysis.',
      image: img1,
      stats: { projects: 18, papers: 42, conferences: 12 },
      unit: 'Sustainable Development Unit',
      publications: 42
    },
    {
      id: 2,
      title: 'Educational Technology & Innovation',
      description: 'Research on e-learning methodologies, educational technology adoption, and digital pedagogy.',
      image: img2,
      stats: { projects: 22, papers: 38, conferences: 15 },
      unit: 'Educational Technology Unit',
      publications: 38
    },
    {
      id: 3,
      title: 'Multilingual Education Studies',
      description: 'Investigation of multilingual learning strategies, content adaptation, and culturally sensitive pedagogy.',
      image: img3,
      stats: { projects: 15, papers: 32, conferences: 10 },
      unit: 'Multilingual Education Unit',
      publications: 32
    },
    {
      id: 4,
      title: 'Capacity Development Research',
      description: 'Research on effective capacity building methodologies, training impact assessment, and institutional development.',
      image: img4,
      stats: { projects: 20, papers: 45, conferences: 18 },
      unit: 'Capacity Development Unit',
      publications: 45
    },
    {
      id: 5,
      title: 'International Relations & Policy Studies',
      description: 'Academic research on international relations, diplomacy, policy analysis, and global governance.',
      image: img5,
      stats: { projects: 16, papers: 36, conferences: 14 },
      unit: 'Policy Studies Unit',
      publications: 36
    },
    {
      id: 6,
      title: 'Economic Development Research',
      description: 'Research on economic development theories, feasibility studies, and development economics.',
      image: img6,
      stats: { projects: 14, papers: 30, conferences: 8 },
      unit: 'Economic Studies Unit',
      publications: 30
    }
  ];

  // Academic Research Projects
  const researchProjects = {
    current: [
      {
        id: 1,
        title: 'SDG Implementation Strategies in Higher Education',
        status: 'In Progress',
        progress: 75,
        team: 'Sustainable Development Unit - 8 Researchers',
        timeline: 'Jan 2025 - Dec 2025',
        description: 'Investigating how universities can effectively integrate SDG 2030 into curricula, operations, and community engagement.',
        image: img7,
        unit: 'Sustainable Development Unit',
        funding: 'International Research Grant',
        publications: 3
      },
      {
        id: 2,
        title: 'Effectiveness of Multilingual e-Learning',
        status: 'Testing',
        progress: 90,
        team: 'Multilingual Education Unit - 6 Researchers',
        timeline: 'Mar 2025 - Aug 2025',
        description: 'Longitudinal study on learning outcomes in multilingual e-learning environments across three language groups.',
        image: img8,
        unit: 'Multilingual Education Unit',
        funding: 'Educational Research Foundation',
        publications: 2
      },
      {
        id: 3,
        title: 'AI Applications in Academic Assessment',
        status: 'Development',
        progress: 60,
        team: 'Educational Technology Unit - 7 Researchers',
        timeline: 'Feb 2025 - Nov 2025',
        description: 'Developing and testing AI-powered tools for automated assessment and personalized feedback in higher education.',
        image: img9,
        unit: 'Educational Technology Unit',
        funding: 'Technology Innovation Grant',
        publications: 4
      }
    ],
    completed: [
      {
        id: 4,
        title: 'Capacity Development Impact Assessment Framework',
        status: 'Completed',
        progress: 100,
        team: 'Capacity Development Unit - 5 Researchers',
        timeline: 'Jun 2024 - Jan 2025',
        description: 'Developed a comprehensive framework for measuring the impact of capacity development programs across different contexts.',
        image: img10,
        results: 'Framework adopted by 3 international development organizations. 4 journal publications.',
        publications: 4,
        unit: 'Capacity Development Unit'
      },
      {
        id: 5,
        title: 'Women in Leadership: Regional Study',
        status: 'Completed',
        progress: 100,
        team: 'Policy Studies Unit - 6 Researchers',
        timeline: 'Sep 2024 - Feb 2025',
        description: 'Comparative study of women\'s leadership advancement in Middle East, Africa, and Europe.',
        image: img8,
        results: 'Published in Adam International Journal. Presented at 3 international conferences. 2 policy briefs.',
        publications: 3,
        unit: 'Policy Studies Unit'
      },
      {
        id: 6,
        title: 'Economic Feasibility Study Methodology',
        status: 'Completed',
        progress: 100,
        team: 'Economic Studies Unit - 4 Researchers',
        timeline: 'May 2024 - Mar 2025',
        description: 'Development of standardized methodology for conducting economic feasibility studies in development contexts.',
        image: img9,
        results: 'Methodology published as guide. Used in 5 major development projects. 3 research papers.',
        publications: 3,
        unit: 'Economic Studies Unit'
      }
    ]
  };

  // Research Team from documents
  const researchTeam = [
    {
      id: 1,
      name: 'Prof. Dr. Abdulnasir Munir',
      role: 'Director, Economic Research',
      expertise: 'Development Economics, Policy Analysis, Feasibility Studies',
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 48,
      unit: 'Economic Studies Unit',
      citations: 1250
    },
    {
      id: 2,
      name: 'Prof. Dr. Ayşe Demir',
      role: 'Director, Sustainable Development Research',
      expertise: 'SDG Implementation, Sustainability Metrics, Development Policy',
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 52,
      unit: 'Sustainable Development Unit',
      citations: 1870
    },
    {
      id: 3,
      name: 'Prof. Dr. Samir Albuny',
      role: 'Director, Educational Technology',
      expertise: 'AI in Education, Digital Pedagogy, Learning Analytics',
      image: 'https://images.unsplash.com/photo-1531427186627-4fd6f1e5f6ed?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 39,
      unit: 'Educational Technology Unit',
      citations: 980
    },
    {
      id: 4,
      name: 'Prof. Dr. Hamman Adam',
      role: 'Director, Policy Studies',
      expertise: 'International Relations, Diplomacy, Policy Analysis',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 45,
      unit: 'Policy Studies Unit',
      citations: 1560
    },
    {
      id: 5,
      name: 'Dr. Amanda White',
      role: 'Lead Researcher, Multilingual Education',
      expertise: 'Multilingual Learning, Content Adaptation, Instructional Design',
      image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 31,
      unit: 'Multilingual Education Unit',
      citations: 720
    },
    {
      id: 6,
      name: 'Prof. Dr. Sarah Wilson',
      role: 'Lead Researcher, Capacity Development',
      expertise: 'Training Impact, Institutional Development, Capacity Building',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      publications: 37,
      unit: 'Capacity Development Unit',
      citations: 890
    }
  ];

  // Recent Publications
  const recentPublications = [
    {
      id: 1,
      title: 'Integrating SDG 2030 into Higher Education Curricula: Challenges and Opportunities',
      authors: 'Demir, A., Munir, A., White, A.',
      journal: 'Adam International Journal, Vol. 2, Issue 1',
      year: 2026,
      link: '#',
      citations: 18,
      doi: '10.5281/zenodo.14725838'
    },
    {
      id: 2,
      title: 'Effectiveness of Multilingual e-Learning: A Comparative Study',
      authors: 'White, A., Albuny, S., Chen, S.',
      journal: 'Journal of Multilingual Education',
      year: 2025,
      link: '#',
      citations: 24,
      doi: '10.1016/j.jme.2025.03.005'
    },
    {
      id: 3,
      title: 'AI-Powered Assessment: Accuracy and Student Perceptions',
      authors: 'Albuny, S., Wilson, S., Bukairy, N.',
      journal: 'Educational Technology Research',
      year: 2025,
      link: '#',
      citations: 15,
      doi: '10.1007/s11423-025-10234-5'
    },
    {
      id: 4,
      title: 'Capacity Development Impact: A Framework for Measurement',
      authors: 'Wilson, S., Demir, A., Al-Rahman, N.',
      journal: 'Development Studies Quarterly',
      year: 2025,
      link: '#',
      citations: 22,
      doi: '10.1080/00220388.2025.1234567'
    },
    {
      id: 5,
      title: 'Women in Leadership: Barriers and Success Factors in MENA',
      authors: 'Adams, N., Demir, A., Garcia, M.',
      journal: 'Gender & Development',
      year: 2026,
      link: '#',
      citations: 12,
      doi: '10.1080/13552074.2026.1234567'
    },
    {
      id: 6,
      title: 'Economic Feasibility Studies in Development Contexts: A Methodology',
      authors: 'Munir, A., Bukairy, N., Kim, R.',
      journal: 'Development Economics Review',
      year: 2025,
      link: '#',
      citations: 31,
      doi: '10.1016/j.dev.2025.02.012'
    }
  ];

  // Research Units
  const researchUnits = [
    { name: 'Sustainable Development Unit', projects: 18, papers: 42, conferences: 12, lead: 'Prof. Dr. Ayşe Demir' },
    { name: 'Educational Technology Unit', projects: 22, papers: 38, conferences: 15, lead: 'Prof. Dr. Samir Albuny' },
    { name: 'Multilingual Education Unit', projects: 15, papers: 32, conferences: 10, lead: 'Dr. Amanda White' },
    { name: 'Capacity Development Unit', projects: 20, papers: 45, conferences: 18, lead: 'Prof. Dr. Sarah Wilson' },
    { name: 'Policy Studies Unit', projects: 16, papers: 36, conferences: 14, lead: 'Prof. Dr. Hamman Adam' },
    { name: 'Economic Studies Unit', projects: 14, papers: 30, conferences: 8, lead: 'Prof. Dr. Abdulnasir Munir' }
  ];

  return (
    <div className="research-development-page">
      {/* Hero Section - Updated */}
      <section className="rd-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Adam Center for Academic Research</h1>
            <p>Advancing knowledge through interdisciplinary research in sustainable development, education, and policy</p>
            <div className="hero-stats">
              <div className="stat">
                <span className="number">6</span>
                <span className="label">Research Units</span>
              </div>
              <div className="stat">
                <span className="number">50+</span>
                <span className="label">Researchers</span>
              </div>
              <div className="stat">
                <span className="number">200+</span>
                <span className="label">Publications</span>
              </div>
              <div className="stat">
                <span className="number">30+</span>
                <span className="label">Conferences</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <img src={heroImage} alt="Research Center" loading="lazy" />
          </div>
        </div>
      </section>

      {/* Research Units Section */}
      <section 
        className="research-units"
        ref={el => sectionRefs.current.units = el}
        data-section="units"
      >
        <div className={`container ${visibleSections.units ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Research Units</h2>
            <p>Specialized academic units focused on critical global challenges</p>
          </div>
          <div className="units-grid">
            {researchUnits.map((unit, index) => (
              <div key={index} className="unit-card">
                <h3>{unit.name}</h3>
                <p className="unit-lead">Led by: {unit.lead}</p>
                <div className="unit-stats">
                  <div className="unit-stat">
                    <span className="stat-number">{unit.projects}</span>
                    <span className="stat-label">Projects</span>
                  </div>
                  <div className="unit-stat">
                    <span className="stat-number">{unit.papers}</span>
                    <span className="stat-label">Papers</span>
                  </div>
                  <div className="unit-stat">
                    <span className="stat-number">{unit.conferences}</span>
                    <span className="stat-label">Conferences</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section 
        className="research-areas"
        ref={el => sectionRefs.current.areas = el}
        data-section="areas"
      >
        <div className={`container ${visibleSections.areas ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Research Focus Areas</h2>
            <p>Exploring solutions for global challenges through academic research</p>
          </div>
          <div className="areas-grid">
            {researchAreas.map(area => (
              <div key={area.id} className="area-card">
                <div className="area-image">
                  <img src={area.image} alt={area.title} loading="lazy" />
                  <div className="area-unit">{area.unit}</div>
                </div>
                <div className="area-content">
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                  <div className="area-stats">
                    <div className="stat">
                      <span className="number">{area.stats.projects}</span>
                      <span className="label">Projects</span>
                    </div>
                    <div className="stat">
                      <span className="number">{area.stats.papers}</span>
                      <span className="label">Papers</span>
                    </div>
                    <div className="stat">
                      <span className="number">{area.stats.conferences}</span>
                      <span className="label">Conferences</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Projects */}
      <section 
        className="research-projects"
        ref={el => sectionRefs.current.projects = el}
        data-section="projects"
      >
        <div className={`container ${visibleSections.projects ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Research Projects</h2>
            <p>Current and completed academic research initiatives</p>
          </div>
          
          <div className="projects-tabs">
            <button 
              className={`tab ${activeTab === 'current' ? 'active' : ''}`}
              onClick={() => setActiveTab('current')}
              aria-label="Show current projects"
            >
              Current Projects ({researchProjects.current.length})
            </button>
            <button 
              className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
              aria-label="Show completed projects"
            >
              Completed Projects ({researchProjects.completed.length})
            </button>
          </div>

          <div className="projects-grid">
            {researchProjects[activeTab].map(project => (
              <div key={project.id} className="project-card">
                <div className="project-image">
                  <img src={project.image} alt={project.title} loading="lazy" />
                  <div className="project-status">{project.status}</div>
                  <div className="project-unit">{project.unit}</div>
                </div>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  
                  <div className="project-details">
                    <div className="detail">
                      <span className="label">Team:</span>
                      <span className="value">{project.team}</span>
                    </div>
                    <div className="detail">
                      <span className="label">Timeline:</span>
                      <span className="value">{project.timeline}</span>
                    </div>
                    {project.funding && (
                      <div className="detail">
                        <span className="label">Funding:</span>
                        <span className="value">{project.funding}</span>
                      </div>
                    )}
                    
                    {project.progress < 100 && (
                      <div className="progress-container">
                        <div className="progress-label">Progress: {project.progress}%</div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{width: `${project.progress}%`}}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {project.results && (
                      <div className="project-results">
                        <span className="label">Results:</span>
                        <span className="value">{project.results}</span>
                      </div>
                    )}
                    
                    {project.publications && (
                      <div className="project-publications">
                        <span className="label">Publications:</span>
                        <span className="value">{project.publications} papers</span>
                      </div>
                    )}
                  </div>
                  
                  <Link to={`/research/${project.id}`} className="project-btn">View Research Details</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Team */}
      <section 
        className="research-team"
        ref={el => sectionRefs.current.team = el}
        data-section="team"
      >
        <div className={`container ${visibleSections.team ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Lead Researchers</h2>
            <p>Distinguished scholars leading our academic research initiatives</p>
          </div>
          
          <div className="team-grid">
            {researchTeam.map(member => (
              <div key={member.id} className="team-member">
                <div className="member-image">
                  <img src={member.image} alt={member.name} loading="lazy" />
                </div>
                <div className="member-info">
                  <h3>{member.name}</h3>
                  <p className="role">{member.role}</p>
                  <p className="unit-badge">{member.unit}</p>
                  <p className="expertise">{member.expertise}</p>
                  <div className="member-stats">
                    <span className="publications">{member.publications} publications</span>
                    <span className="citations">{member.citations} citations</span>
                  </div>
                </div>
                <Link to={`/researcher/${member.id}`} className="profile-btn">View Profile</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section 
        className="publications-section"
        ref={el => sectionRefs.current.publications = el}
        data-section="publications"
      >
        <div className={`container ${visibleSections.publications ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Recent Publications</h2>
            <p>Peer-reviewed research from our academic units</p>
          </div>
          
          <div className="publications-list">
            {recentPublications.map(publication => (
              <div key={publication.id} className="publication-item">
                <div className="pub-content">
                  <h3>{publication.title}</h3>
                  <p className="authors">{publication.authors}</p>
                  <p className="journal">{publication.journal}, {publication.year}</p>
                  <div className="pub-meta">
                    <span className="citations">📚 {publication.citations} citations</span>
                    {publication.doi && <span className="doi">DOI: {publication.doi}</span>}
                  </div>
                </div>
                <a href={publication.link} className="download-btn">Download PDF</a>
              </div>
            ))}
          </div>
          
          <div className="view-all-container">
            <Link to="/publications" className="view-all-btn">View All Publications (200+)</Link>
          </div>
        </div>
      </section>

      {/* Research Guidelines */}
      <section 
        className="guidelines-section"
        ref={el => sectionRefs.current.guidelines = el}
        data-section="guidelines"
      >
        <div className={`container ${visibleSections.guidelines ? 'visible' : ''}`}>
          <div className="guidelines-content">
            <h2>Research Publication Guidelines</h2>
            <p>Our research follows rigorous academic standards for quality and impact</p>
            <div className="guidelines-grid">
              <div className="guideline-item">
                <h4>Peer Review Process</h4>
                <p>All publications undergo double-blind peer review by international experts</p>
              </div>
              <div className="guideline-item">
                <h4>Ethical Standards</h4>
                <p>Research conducted in accordance with international ethical guidelines</p>
              </div>
              <div className="guideline-item">
                <h4>Open Access</h4>
                <p>Commitment to making research freely available to scholars worldwide</p>
              </div>
              <div className="guideline-item">
                <h4>Impact Focus</h4>
                <p>Research aligned with real-world challenges and policy relevance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section 
        className="collaboration-section"
        ref={el => sectionRefs.current.collaboration = el}
        data-section="collaboration"
      >
        <div className={`container ${visibleSections.collaboration ? 'visible' : ''}`}>
          <div className="collab-content">
            <div className="collab-text">
              <h2>Research Collaboration</h2>
              <p>We partner with universities and research centers worldwide to advance knowledge and innovation</p>
              <div className="collab-stats">
                <div className="stat">
                  <span className="number">15+</span>
                  <span className="label">University Partners</span>
                </div>
                <div className="stat">
                  <span className="number">20+</span>
                  <span className="label">Research Centers</span>
                </div>
                <div className="stat">
                  <span className="number">30+</span>
                  <span className="label">Countries</span>
                </div>
              </div>
              <Link to="/partners" className="collab-btn">Explore Research Partnerships</Link>
            </div>
            <div className="collab-visual">
              <img src={collabImage} alt="Research Collaboration" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Research Facilities */}
      <section 
        className="facilities-section"
        ref={el => sectionRefs.current.facilities = el}
        data-section="facilities"
      >
        <div className={`container ${visibleSections.facilities ? 'visible' : ''}`}>
          <div className="section-header">
            <h2>Research Facilities</h2>
            <p>Resources and infrastructure supporting academic research</p>
          </div>
          
          <div className="facilities-grid">
            <div className="facility-card">
              <img src={FacilitiesImage1} alt="Research Library" loading="lazy" />
              <h3>Adam Research Library</h3>
              <p>Digital library with 100,000+ academic resources, journals, and research papers in applied and social sciences</p>
            </div>
            
            <div className="facility-card">
              <img src={FacilitiesImage2} alt="Data Analysis Center" loading="lazy" />
              <h3>Data Analysis Center</h3>
              <p>Advanced statistical software and computing resources for quantitative and qualitative research</p>
            </div>
            
            <div className="facility-card">
              <img src={FacilitiesImage3} alt="Collaboration Hub" loading="lazy" />
              <h3>International Research Hub</h3>
              <p>Virtual collaboration spaces connecting researchers across 30+ countries for joint projects</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchDevelopment;